import "./style.scss";

import FileFailed from "assets/img/svg/fileFailed";
import FileSuccess from "assets/img/svg/fileSuccess";
import UploadIdle from "assets/img/svg/uploadIdle";
import UploadRejected from "assets/img/svg/UploadRejected";
import UploadSuccess from "assets/img/svg/uploadSuccess";
import { fileWeightValidator } from "helpers/importData-helpers";
import React, { useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";

import variables from "../../../assets/styles/_variables.module.scss";

type DropzoneProps = {
    setFile: React.Dispatch<React.SetStateAction<any>>;
    file?: File[];
    nextStep?: () => void;
    autoValidate?: boolean;
    acceptedFilesTypeList?: { [key: string]: string[] };
    style?: React.CSSProperties;
    compact?: boolean;
    error?: boolean;
    maxSize?: number;
    customValidator?: (file: File) => { code: string; message: string } | null;
    rejectedHint?: string;
    /** When set, replaces the default ImportData.Warning hint under the dropzone. */
    hintText?: string;
    /** Skip file-type filter; rely on customValidator only (e.g. Interforum EDI by file name). */
    acceptAnyFile?: boolean;
};

const acceptedFilesTypeListDefault = {
    "text/csv": [".csv"],
    // "application/pdf": [".pdf"]
    // "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
    // "application/vnd.ms-excel": [".xls"]
};

const MAX_SIZE = 5 * 1000000; // 5MB

//TODO: Add ability to import xls files
const DropZoneCSV = (props: DropzoneProps) => {
    const {
        setFile,
        file,
        nextStep = () => {},
        autoValidate = false,
        acceptedFilesTypeList = acceptedFilesTypeListDefault,
        style = {},
        compact = false,
        error = false,
        maxSize = MAX_SIZE,
        customValidator,
        rejectedHint,
        hintText,
        acceptAnyFile = false,
    } = props;
    const [t] = useTranslation();
    const fileExtensions = Object.values(acceptedFilesTypeList).flat();
    const uniqueFileExtensions = [...new Set(fileExtensions.map((ext) => ext.toUpperCase()))];

    const { getRootProps, getInputProps, open, fileRejections, acceptedFiles, isDragActive } = useDropzone({
        noKeyboard: true,
        validator: (file: File) => {
            const weightError = fileWeightValidator(file, maxSize);
            if (weightError) {
                return weightError;
            }
            return customValidator?.(file) ?? null;
        },
        maxFiles: 1,
        maxSize: maxSize,
        ...(acceptAnyFile ? {} : { accept: acceptedFilesTypeList }),
        onFileDialogOpen() {
            setFile([]);
        },
    });

    // const [hasExecutedOnce, setHasExecutedOnce] = useState(false)
    const [secondsLeft, setSecondsLeft] = useState(5);

    const acceptedFile = acceptedFiles.map((file) => (
        <span className="file-name" key={file.name}>
            {file.name} - {file.size} bytes
        </span>
    ));

    const rejectedFile = fileRejections.map(({ file }) => (
        <span className="file-name" key={file.name}>
            {file.name} - {file.size} bytes
        </span>
    ));

    const nextStepFn = useCallback(() => nextStep(), [nextStep]);

    useEffect(() => {
        if (acceptedFile.length !== 0) {
            setFile(acceptedFiles);

            const interval = setInterval(() => setSecondsLeft((prevSeconds) => (prevSeconds -= 1)), 1000);
            if (secondsLeft === 0) {
                nextStepFn();
            }

            return () => clearInterval(interval);
        }
    }, [acceptedFiles, acceptedFile, setFile, nextStepFn]);

    return (
        <div
            {...getRootProps({
                className: `upload-csv-wrapper ${
                    !isDragActive && fileRejections.length !== 0
                        ? "error"
                        : !isDragActive && acceptedFiles.length !== 0
                          ? "accept"
                          : "idle"
                }`,
            })}
            style={{
                ...style,
                ...(acceptedFiles.length === 0 && error
                    ? {
                          backgroundColor: "var(--danger-light)",
                          borderColor: "var(--danger)",
                      }
                    : {}),
            }}
        >
            <input className="input-zone" {...getInputProps()} onClick={() => setFile([])} />
            {(isDragActive || (acceptedFiles.length === 0 && fileRejections.length === 0)) && (
                <div className="idle">
                    <UploadIdle color={variables["foreground"]} />
                    <div>
                        {isDragActive ? (
                            <span>{t("ImportData.ReleaseFiles")}</span>
                        ) : (
                            <>
                                <span>{t("ImportData.DragNDrop")}</span>
                                {!compact && <span>{t("ImportData.Or")}</span>}
                                <button type="button" className="choose-file">
                                    {t("ImportData.ChooseFile")}
                                </button>
                            </>
                        )}
                    </div>
                </div>
            )}
            {!isDragActive && acceptedFile.length !== 0 && (
                <div className="upload">
                    <div>
                        <UploadSuccess />
                    </div>
                    <div className="info success">
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                            }}
                        >
                            <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                                <FileSuccess />
                                {acceptedFile}
                            </div>
                            <span className="clue">{t("Upload.Successful")}</span>
                        </div>
                        {autoValidate && (
                            <p style={{ marginTop: "2rem" }}>
                                {t("ImportData.FileTimerText")}
                                {secondsLeft}s
                            </p>
                        )}
                    </div>
                </div>
            )}
            {!isDragActive && fileRejections.length !== 0 && (
                <div className="upload">
                    <div>
                        <UploadRejected />
                    </div>
                    <div className="info rejected">
                        <FileFailed />
                        {rejectedFile}
                        <span className="clue">{rejectedHint ?? t("ImportData.UploadRejected")}</span>
                    </div>
                </div>
            )}
            {!compact && (isDragActive || acceptedFile.length) === 0 && hintText !== undefined && (
                <p className="hint">{hintText}</p>
            )}
            {!compact && (isDragActive || acceptedFile.length) === 0 && hintText === undefined && (
                <p className="hint">
                    {t("ImportData.Warning", {
                        fileTypes: uniqueFileExtensions.join(", "),
                    })}
                    {fileExtensions.includes(".csv") && t("ImportData.WarningCSV")}
                    {fileRejections.some((o) => o.errors.some((err) => err.code === "file-too-large"))
                        ? t("ImportData.WarningFilesize", {
                              size: `${maxSize / 1000000}MB`,
                          })
                        : null}
                </p>
            )}
        </div>
    );
};
export default DropZoneCSV;
